import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { claimsApi, ApiError } from '../../../services/api';
import styles from './ClaimsSummaryScreen.module.css';

interface ClaimsSummaryData {
  totalCosts: number;
  totalCostsTrend: number;
  monthlyData: { month: string; amount: number; claimCount?: number }[];
  costsByType: {
    consultation: number;
    labTests: number;
    medication: number;
    therapy: number;
  };
  totalClaims: number;
  averageClaimCost: number;
  totalSavings: number;
  recentClaim: {
    type: string;
    provider: string;
    date?: string;
    amount?: number;
    youOwe?: number;
  };
}

const ClaimsSummaryScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState<ClaimsSummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredDataPoint, setHoveredDataPoint] = useState<number | null>(null);
  const [timeRange, setTimeRange] = useState<'12months' | '6months' | 'ytd'>('12months');

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const fetchSummaryData = async () => {
    setIsLoading(true);
    try {
      // API only has data for years 2022-2024
      const currentYear = Math.min(new Date().getFullYear(), 2024);
      const result = await claimsApi.getSummary({ year: currentYear.toString() });
      
      if (result.success) {
        const summary = result.data.summary;
        const data: ClaimsSummaryData = {
          totalCosts: summary.totalBilled,
          totalCostsTrend: summary.totalCostsTrend,
          monthlyData: summary.monthlyData,
          costsByType: summary.costsByType,
          totalClaims: summary.totalClaims,
          averageClaimCost: summary.averageClaimCost,
          totalSavings: summary.totalSavings,
          recentClaim: result.data.claims[0] ? {
            type: result.data.claims[0].service,
            provider: result.data.claims[0].provider,
            date: result.data.claims[0].date,
            amount: result.data.claims[0].totalAmount,
            youOwe: result.data.claims[0].youOwe
          } : {
            type: 'No recent claims',
            provider: ''
          }
        };
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMaxCostType = () => {
    if (!summaryData) return 0;
    return Math.max(
      summaryData.costsByType.consultation,
      summaryData.costsByType.labTests,
      summaryData.costsByType.medication,
      summaryData.costsByType.therapy
    );
  };

  const getFilteredData = () => {
    if (!summaryData?.monthlyData) return [];
    
    const currentMonth = new Date().getMonth();
    const data = [...summaryData.monthlyData];
    
    switch (timeRange) {
      case '6months':
        return data.slice(Math.max(0, currentMonth - 5), currentMonth + 1);
      case 'ytd':
        return data.slice(0, currentMonth + 1);
      default:
        return data;
    }
  };

  const generateLinePath = (data: { month: string; amount: number }[]) => {
    if (!data || data.length === 0) return '';
    
    const maxAmount = Math.max(...data.map(d => d.amount));
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * 380 + 10;
      const y = 120 - ((d.amount / maxAmount) * 100 + 10);
      return `${x},${y}`;
    });
    
    // Create smooth curve using quadratic bezier curves
    let path = `M ${points[0]}`;
    for (let i = 1; i < points.length; i++) {
      const [x0, y0] = points[i - 1].split(',').map(Number);
      const [x1, y1] = points[i].split(',').map(Number);
      const mx = (x0 + x1) / 2;
      const my = (y0 + y1) / 2;
      path += ` Q ${x0},${y1} ${mx},${my}`;
      if (i === points.length - 1) {
        path += ` T ${x1},${y1}`;
      }
    }
    
    return path;
  };

  const generateAreaPath = (data: { month: string; amount: number }[]) => {
    if (!data || data.length === 0) return '';
    
    const linePath = generateLinePath(data);
    const lastX = 380 + 10;
    return `${linePath} L ${lastX},120 L 10,120 Z`;
  };

  if (isLoading || !summaryData) {
    return (
      <div className={styles.claimsSummaryScreen}>
        <div className={styles.depth0Frame0}>
          <div className={styles.depth1Frame0}>
            {/* Header Skeleton */}
            <div className={styles.depth2Frame0}>
              <div className={styles.skeletonButton} />
              <div className={styles.skeletonTitle} />
            </div>

            {/* Title Skeleton */}
            <div className={styles.depth2Frame1}>
              <div className={styles.skeletonLargeTitle} />
            </div>

            {/* Chart Card Skeleton */}
            <div className={styles.depth2Frame2}>
              <div className={styles.depth3Frame01}>
                <div className={styles.skeletonSmallText} />
                <div className={styles.skeletonLargeNumber} />
                <div className={styles.skeletonChart} />
              </div>
            </div>

            {/* Claims List Skeleton */}
            <div className={styles.depth2Frame5}>
              <div className={styles.skeletonSubtitle} />
            </div>
            <div className={styles.depth2Frame6}>
              <div className={styles.skeletonClaimCard} />
            </div>
            <div className={styles.depth2Frame7}>
              <div className={styles.skeletonSeeAll} />
            </div>

            {/* Service Types Skeleton */}
            <div className={styles.depth2Frame2}>
              <div className={styles.depth3Frame01}>
                <div className={styles.skeletonSmallText} />
                <div className={styles.skeletonLargeNumber} />
                <div className={styles.skeletonServiceTypes} />
              </div>
            </div>

            {/* Stats Row Skeleton */}
            <div className={styles.depth2Frame4}>
              <div className={styles.depth3Frame05}>
                <div className={styles.skeletonStatCard} />
              </div>
              <div className={styles.depth3Frame13}>
                <div className={styles.skeletonStatCard} />
              </div>
              <div className={styles.depth3Frame13}>
                <div className={styles.skeletonStatCard} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.claimsSummaryScreen}>
      <div className={styles.depth0Frame0}>
        <div className={styles.depth1Frame0}>
          <div className={styles.depth2Frame0}>
            <div className={styles.depth3Frame1}>
              <b className={styles.claims}>Claims Summary</b>
            </div>
          </div>

          {/* Total Costs Over Time */}
          <div className={styles.depth2Frame2}>
            <div className={styles.depth3Frame01}>
              <div className={styles.depth1Frame0}>
                <div className={styles.totalCostsOver}>Total Costs Over Time</div>
              </div>
              <div className={styles.depth4Frame1}>
                <b className={styles.b}>{formatCurrency(summaryData.totalCosts)}</b>
              </div>
              <div className={styles.depth4Frame2}>
                <div className={styles.depth5Frame0}>
                  <select 
                    className={styles.timeRangeSelect}
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as any)}
                    aria-label="Select time range for cost chart"
                  >
                    <option value="12months">Last 12 Months</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="ytd">Year to Date</option>
                  </select>
                </div>
                <div className={styles.depth5Frame1}>
                  <TrendingUpIcon className={styles.trendIcon} />
                  <div className={styles.totalCostsOver}>+{summaryData.totalCostsTrend}% from previous year</div>
                </div>
              </div>
              <div className={styles.depth4Frame3}>
                {summaryData.monthlyData && summaryData.monthlyData.length > 0 ? (
                  <div className={styles.chartContainer}>
                    <svg 
                      className={styles.lineChart} 
                      viewBox="0 0 400 120" 
                      onMouseLeave={() => setHoveredDataPoint(null)}
                      role="img"
                      aria-label={`Cost chart showing ${getFilteredData().length} months of data. Total costs: ${formatCurrency(summaryData.totalCosts)}.`}
                    >
                      <title>Monthly Healthcare Costs Over Time</title>
                      <desc>Interactive line chart showing healthcare costs by month. Hover over data points for details.</desc>
                      {/* Grid lines */}
                      <g className={styles.gridLines}>
                        {[0, 25, 50, 75, 100].map((y) => (
                          <line
                            key={y}
                            x1="0"
                            y1={120 - (y * 1.2)}
                            x2="400"
                            y2={120 - (y * 1.2)}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                          />
                        ))}
                      </g>
                      
                      {/* Line path */}
                      <path
                        d={generateLinePath(getFilteredData())}
                        fill="none"
                        stroke="#2563eb"
                        strokeWidth="2"
                        className={styles.linePath}
                      />
                      
                      {/* Area under line */}
                      <path
                        d={generateAreaPath(getFilteredData())}
                        fill="url(#gradient)"
                        opacity="0.1"
                      />
                      
                      {/* Gradient definition */}
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      
                      {/* Data points */}
                      {getFilteredData().map((data, index) => {
                        const filteredData = getFilteredData();
                        const x = (index / (filteredData.length - 1)) * 380 + 10;
                        const maxAmount = Math.max(...filteredData.map(d => d.amount));
                        const y = 120 - ((data.amount / maxAmount) * 100 + 10);
                        const isCurrentMonth = data.month === summaryData.monthlyData[new Date().getMonth()]?.month;
                        const isHovered = hoveredDataPoint === index;
                        
                        return (
                          <g key={index}>
                            {/* Invisible hover area */}
                            <rect
                              x={x - 20}
                              y={0}
                              width={40}
                              height={120}
                              fill="transparent"
                              onMouseEnter={() => setHoveredDataPoint(index)}
                              style={{ cursor: 'pointer' }}
                            />
                            <circle
                              cx={x}
                              cy={y}
                              r={isCurrentMonth || isHovered ? "4" : "3"}
                              fill={isCurrentMonth || isHovered ? "#2563eb" : "#ffffff"}
                              stroke="#2563eb"
                              strokeWidth="2"
                            />
                            {(isCurrentMonth || isHovered) && (
                              <circle
                                cx={x}
                                cy={y}
                                r="8"
                                fill="none"
                                stroke="#2563eb"
                                strokeWidth="1"
                                opacity="0.3"
                              />
                            )}
                            {/* Tooltip */}
                            {isHovered && (
                              <g>
                                <rect
                                  x={x - 60}
                                  y={y - 45}
                                  width={120}
                                  height={35}
                                  rx={4}
                                  fill="#1a1a1a"
                                  fillOpacity={0.9}
                                />
                                <text
                                  x={x}
                                  y={y - 28}
                                  fill="white"
                                  fontSize="12"
                                  textAnchor="middle"
                                  fontWeight="600"
                                >
                                  {data.month}: {formatCurrency(data.amount)}
                                </text>
                                <text
                                  x={x}
                                  y={y - 15}
                                  fill="#ccc"
                                  fontSize="10"
                                  textAnchor="middle"
                                >
                                  {data.claimCount || Math.round(data.amount / 400)} claims
                                </text>
                              </g>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                    
                    {/* Month labels */}
                    <div className={styles.monthLabels}>
                      {getFilteredData().map((data, index) => (
                        <span 
                          key={index} 
                          className={`${styles.monthLabel} ${data.month === summaryData.monthlyData[new Date().getMonth()]?.month ? styles.currentMonth : ''}`}
                        >
                          {data.month}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyChart}>
                    <div className={styles.emptyChartIcon}>
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <path d="M8 40V16L16 24L24 8L32 20L40 12V40H8Z" stroke="#e0e0e0" strokeWidth="2" fill="none"/>
                        <circle cx="16" cy="24" r="2" fill="#e0e0e0"/>
                        <circle cx="24" cy="8" r="2" fill="#e0e0e0"/>
                        <circle cx="32" cy="20" r="2" fill="#e0e0e0"/>
                        <circle cx="40" cy="12" r="2" fill="#e0e0e0"/>
                      </svg>
                    </div>
                    <p className={styles.emptyChartText}>No cost data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Past Claims Section */}
          <div className={styles.depth2Frame5}>
            <b className={styles.claimsSummary}>Past Claims</b>
          </div>

          <div className={styles.depth2Frame6} onClick={() => navigate('/claims/claim-1')}>
            <div className={styles.depth3Frame02}>
              <LocalHospitalIcon className={styles.claimIcon} />
              <div className={styles.depth4Frame11}>
                <div className={styles.depth5Frame02}>
                  <div className={styles.totalCostsOver}>{summaryData.recentClaim.type}</div>
                </div>
                <div className={styles.depth5Frame12}>
                  <div className={styles.cityHospital}>{summaryData.recentClaim.provider}</div>
                </div>
                {summaryData.recentClaim.date && (
                  <div className={styles.claimMeta}>
                    <span className={styles.claimDate}>
                      Filed: {new Date(summaryData.recentClaim.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {summaryData.recentClaim.amount && (
                      <span className={styles.claimCost}>
                        Cost: {formatCurrency(summaryData.recentClaim.amount)} | You paid: {formatCurrency(summaryData.recentClaim.youOwe || 0)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.claimActions}>
              <span className={styles.viewDetails}>View Details ›</span>
              <ChevronRightIcon className={styles.chevronIcon} />
            </div>
          </div>

          <div className={styles.depth2Frame7} onClick={() => navigate('/past-claims')}>
            <div className={styles.depth3Frame03}>
              <div className={styles.depth4Frame11}>
                <div className={styles.depth5Frame02}>
                  <div className={styles.seeAllClaims}>See last {summaryData.totalClaims} claims</div>
                </div>
              </div>
            </div>
            <ChevronRightIcon className={styles.chevronIcon} />
          </div>

          {/* Costs by Service Type */}
          <div className={styles.depth2Frame2}>
            <div className={styles.depth3Frame01}>
              <div className={styles.depth1Frame0}>
                <div className={styles.totalCostsOver}>Costs by Service Type</div>
              </div>
              <div className={styles.depth4Frame1}>
                <b className={styles.b}>{formatCurrency(getMaxCostType())}</b>
              </div>
              <div className={styles.depth4Frame2}>
                <div className={styles.depth5Frame0}>
                  <div className={styles.last12Months}>Last 12 Months</div>
                </div>
                <div className={styles.depth5Frame1}>
                  <TrendingUpIcon className={styles.trendIcon} />
                  <div className={styles.totalCostsOver}>+10%</div>
                </div>
              </div>
              <div className={styles.depth4Frame31}>
                <button 
                  className={styles.serviceTypeRow}
                  onClick={() => navigate('/past-claims?filter=consultation')}
                  aria-label={`Consultation: ${formatCurrency(summaryData.costsByType.consultation)}, ${Math.round((summaryData.costsByType.consultation / summaryData.totalCosts) * 100)}% of total spend. Click to view consultation claims.`}
                >
                  <div className={styles.depth5Frame05}>
                    <div className={styles.depth6Frame01}>
                      <b className={styles.serviceType}>Consultation</b>
                      <span className={styles.serviceAmount}>{formatCurrency(summaryData.costsByType.consultation)}</span>
                    </div>
                    <div className={styles.depth6Frame12}>
                      <div 
                        className={styles.depth7Frame0} 
                        style={{ width: `${(summaryData.costsByType.consultation / getMaxCostType()) * 100}%` }}
                      />
                    </div>
                    <div className={styles.servicePercentage}>{Math.round((summaryData.costsByType.consultation / summaryData.totalCosts) * 100)}% of total spend</div>
                  </div>
                </button>
                <button 
                  className={styles.serviceTypeRow}
                  onClick={() => navigate('/past-claims?filter=lab-tests')}
                  aria-label={`Lab Tests: ${formatCurrency(summaryData.costsByType.labTests)}, ${Math.round((summaryData.costsByType.labTests / summaryData.totalCosts) * 100)}% of total spend. Click to view lab test claims.`}
                >
                  <div className={styles.depth5Frame05}>
                    <div className={styles.depth6Frame01}>
                      <b className={styles.serviceType}>Lab Tests</b>
                      <span className={styles.serviceAmount}>{formatCurrency(summaryData.costsByType.labTests)}</span>
                    </div>
                    <div className={styles.depth6Frame12}>
                      <div 
                        className={styles.depth7Frame01} 
                        style={{ width: `${(summaryData.costsByType.labTests / getMaxCostType()) * 100}%` }}
                      />
                    </div>
                    <div className={styles.servicePercentage}>{Math.round((summaryData.costsByType.labTests / summaryData.totalCosts) * 100)}% of total spend</div>
                  </div>
                </button>
                <button 
                  className={styles.serviceTypeRow}
                  onClick={() => navigate('/past-claims?filter=medication')}
                  aria-label={`Medication: ${formatCurrency(summaryData.costsByType.medication)}, ${Math.round((summaryData.costsByType.medication / summaryData.totalCosts) * 100)}% of total spend. Click to view medication claims.`}
                >
                  <div className={styles.depth5Frame05}>
                    <div className={styles.depth6Frame01}>
                      <b className={styles.serviceType}>Medication</b>
                      <span className={styles.serviceAmount}>{formatCurrency(summaryData.costsByType.medication)}</span>
                    </div>
                    <div className={styles.depth6Frame12}>
                      <div 
                        className={styles.depth7Frame02} 
                        style={{ width: `${(summaryData.costsByType.medication / getMaxCostType()) * 100}%` }}
                      />
                    </div>
                    <div className={styles.servicePercentage}>{Math.round((summaryData.costsByType.medication / summaryData.totalCosts) * 100)}% of total spend</div>
                  </div>
                </button>
                <button 
                  className={styles.serviceTypeRow}
                  onClick={() => navigate('/past-claims?filter=therapy')}
                  aria-label={`Therapy: ${formatCurrency(summaryData.costsByType.therapy)}, ${Math.round((summaryData.costsByType.therapy / summaryData.totalCosts) * 100)}% of total spend. Click to view therapy claims.`}
                >
                  <div className={styles.depth5Frame05}>
                    <div className={styles.depth6Frame01}>
                      <b className={styles.serviceType}>Therapy</b>
                      <span className={styles.serviceAmount}>{formatCurrency(summaryData.costsByType.therapy)}</span>
                    </div>
                    <div className={styles.depth6Frame12}>
                      <div 
                        className={styles.depth7Frame03} 
                        style={{ width: `${(summaryData.costsByType.therapy / getMaxCostType()) * 100}%` }}
                      />
                    </div>
                    <div className={styles.servicePercentage}>{Math.round((summaryData.costsByType.therapy / summaryData.totalCosts) * 100)}% of total spend</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Row */}
          <div className={styles.depth2Frame4}>
            <div className={styles.depth3Frame05}>
              <div className={styles.depth1Frame0}>
                <div className={styles.totalCostsOver}>Total Claims</div>
              </div>
              <div className={styles.depth4Frame14}>
                <b className={styles.b2}>{summaryData.totalClaims}</b>
              </div>
              <div className={styles.statComparison}>vs. {Math.round(summaryData.totalClaims * 0.8)} last year</div>
            </div>
            <div className={styles.depth3Frame13}>
              <div className={styles.depth1Frame0}>
                <div className={styles.totalCostsOver}>Average Claim Cost</div>
              </div>
              <div className={styles.depth4Frame14}>
                <b className={styles.b2}>{formatCurrency(summaryData.averageClaimCost)}</b>
              </div>
              <div className={styles.statComparison}>14% higher than similar users</div>
            </div>
            <div className={styles.depth3Frame13}>
              <div className={styles.depth1Frame0}>
                <div className={styles.totalCostsOver}>Total Savings</div>
              </div>
              <div className={styles.depth4Frame14}>
                <b className={styles.b2}>{formatCurrency(summaryData.totalSavings)}</b>
              </div>
              <div className={styles.statComparison}>from Mario PriceCheck guidance</div>
            </div>
          </div>

          {/* Action Buttons Section */}
          <div className={styles.actionSection}>
            <h3 className={styles.actionTitle}>Take Action</h3>
            <div className={styles.actionButtons}>
              <button className={styles.actionButton} onClick={() => window.alert('Export functionality coming soon')}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 14L14 10M10 14L6 10M10 14V3M17 14V17H3V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export for HSA/Taxes
              </button>
              <button className={styles.actionButton} onClick={() => navigate('/dispute-claim')}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M9 11L9 7M9 15H9.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Dispute a Claim
              </button>
              <button className={styles.actionButton} onClick={() => navigate('/coverage-checker')}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M9 5H7C5.89543 5 5 5.89543 5 7V13C5 14.1046 5.89543 15 7 15H13C14.1046 15 15 14.1046 15 13V7C15 5.89543 14.1046 5 13 5H11M9 5C9 6.10457 9.89543 7 11 7C11 8.10457 10.1046 9 9 9C7.89543 9 7 8.10457 7 7C7 5.89543 7.89543 5 9 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Coverage Checker
              </button>
            </div>
          </div>

          {/* Personalized Insight */}
          <div className={styles.insightSection}>
            <div className={styles.insightIcon}>💡</div>
            <div className={styles.insightContent}>
              <h4 className={styles.insightTitle}>Cost Saving Tip</h4>
              <p className={styles.insightText}>
                Your biggest spend is on therapy ({formatCurrency(summaryData.costsByType.therapy)}). 
                Want to find lower-cost providers nearby that accept your insurance?
              </p>
              <button className={styles.insightAction} onClick={() => navigate('/search?type=providers&specialty=therapy')}>
                Find Therapy Providers →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsSummaryScreen;